import {
  Avatar,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { useEffect, useRef, useState } from "react";
import RecorderButton from "./RecorderButton";
import MessageBubble from "./MessageBubble";
import { sendAudio } from "../api";

const PRIMARY = "#3A54AF";
const SECONDARY = "#f44336";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const handleAudio = async (blob) => {
    const audioURL = URL.createObjectURL(blob);

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        role: "user",
        text: "Transcribing your voice...",
        audio: audioURL,
        pending: true,
      },
    ]);

    setLoading(true);

    try {
      const data = await sendAudio(blob);

      const aiAudio = new Audio(data.audioUrl);
      aiAudio.play().catch(() => {});

      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "user",
          text: data.userText,
          audio: audioURL,
        },
        {
          role: "assistant",
          text: data.reply,
          audio: data.audioUrl,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          text: "Something went wrong. Please try again.",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        background: "#f5f7ff",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "1080px", // 🔥 increase from 560 → 900
          height: "100vh",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderLeft: "1px solid #e5e7eb",
          borderRight: "1px solid #e5e7eb",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: PRIMARY,
            color: "#fff",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.8)" }}>
              <img src="/logo.png" width={24} height={24} alt="Assistant" />
            </Avatar>

            <Box>
              <Typography fontWeight={700}>Conversation Partner</Typography>
              <Typography fontSize={12} sx={{ opacity: 0.8 }}>
                Speak and get instant replies
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            py: 2,
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                mt: 10,
                color: "#6b7280",
              }}
            >
              <Typography variant="h6">Start Speaking 🎙️</Typography>
              <Typography fontSize={14}>Tap the mic and talk to AI</Typography>
            </Box>
          )}

          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} />
          ))}

          {loading && (
            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
              <CircularProgress size={16} sx={{ color: PRIMARY }} />
              <Typography fontSize={13}>AI is thinking...</Typography>
            </Stack>
          )}

          <div ref={endRef} />
        </Box>

        {/* Recorder */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          <RecorderButton onRecorded={handleAudio} />
        </Box>
      </Paper>
    </Box>
  );
}
